import { useState } from "react";
import { useNavigate } from "react-router";
import { Toolbar } from "../../../design-system/components/Toolbar/Toolbar.jsx";
import styles from "../../workshops/toolbar/toolbarContainer.css";
import { ROUTES } from "../../../routes.js";
import Button from "../../../design-system/components/Button/Button.jsx";
import { Variants } from "../../../design-system/components/Button/Variants.jsx";
import tokens from "../../../design-system/tokens.js";
import Dialog from "../../../design-system/components/Dialog/Dialog.jsx";
import { MODELS, useModel } from "../../../api";
import { useSparkContext } from "../../workshops/context/SparkContext.jsx";

/**
 * Renders an ActionButton component that conditionally returns different buttons
 * based on the user's state (`me`).
 *
 * If the user isn't logged in, we return the signInButton. If he is, and has an email (teacher or admin)
 * we return the button to directly create a workshop.
 *
 * @param {Object} props - The component props.
 * @param {Object|null} props.me - The current user object.
 * @param {Function} props.setOpen - A function to update the open state of a modal or UI component.
 * @returns {JSX.Element|null} A button element or null depending on the conditions.
 */
const ActionButton = ({ me, setOpen }) => {
  const navigate = useNavigate();
  if (!me)
    return (
      <Button
        onClick={() => {
          setOpen(false);
          navigate(ROUTES.login.getUrl());
        }}
        type="button"
        variant={Variants.PRIMARY}
      >
        Anmelden
      </Button>
    );
  if (me.email)
    return (
      <Button
        onClick={() => {
          setOpen(false);
          navigate(ROUTES.lernreisenCreate.getUrl());
        }}
        type="button"
        variant={Variants.PRIMARY}
      >
        Lernreise erstellen
      </Button>
    );
  return null;
};

/**
 * Renders the ToolbarContainer component that includes a toolbar with customizable buttons
 * and a dialog shown when a disabled button is clicked.
 *
 * @return {JSX.Element} The rendered ToolbarContainer component.
 */
export function ToolbarContainer() {
  const { spark } = useSparkContext();
  const me = useModel(MODELS.ME);

  const getActionText = () => {
    if (!me)
      return "Diese Funktionalität ist in der Demo nicht verfügbar. Melde dich doch an und lege richtig los - dauert nur 30 Sekunden und ist DSGVO-konform :).";
    if (me.email)
      return "Diese Funktionalität ist in der Demo nicht verfügbar. Erstelle deine eigene Lernreise für die komplette Digital Sparks Erfahrung!";
    return null;
  };

  const baseUrl = ROUTES.demoLernreise.getUrl({ sparkSlug: spark?.slug });
  const leaveTo = ROUTES.demoHome.getUrl();

  // Handle state for dialog
  const [open, setOpen] = useState(false);

  function handleOpen() {
    setOpen(true);
  }

  return (
    <div className={styles.toolbarContainer}>
      <Toolbar
        showLibraryButton={spark.defaultSettings?.libraryEnabled}
        showUploadButton={false}
        showLernreiseAdminButtons
        baseUrl={baseUrl}
        leaveTo={leaveTo}
        adminButtonsDisabled
        onDisabledButtonClick={handleOpen}
      />
      <Dialog
        open={open}
        setOpen={setOpen}
        title={"In Demo nicht verfügbar!"}
        buttons={[
          <Button
            key="secondary-button"
            onClick={() => setOpen(false)}
            type="button"
            variant={Variants.SECONDARY}
          >
            Schließen
          </Button>,
          <ActionButton key="action-button" me={me} setOpen={setOpen} />,
        ]}
      >
        <div>
          <p
            style={{
              marginBottom: tokens.space["1x"],
            }}
          >
            {getActionText()}
          </p>
        </div>
      </Dialog>
    </div>
  );
}
